import type { Dispatch, SetStateAction } from 'react';
import { useEffect, useState } from 'react';

import type { FilterOption } from '../../../types/FilterOption';

import { NetworkStatus } from '@apollo/client/core';
import { useQuery } from '@apollo/client/react';

import { GetCollectionOptionsDocument } from '../../../gql';
import { GraphClientNames } from '../../../types/GQLClientNames';
import { toFilterOptions } from '../../../utils/PurchaseOrdersUtils/filterHelpers';

const COLLECTIONS_PAGE_SIZE = 50;

interface UseCollectionsFilterResult {
    /** The cumulative options that have been queried */
    collectionsOptions: Array<FilterOption>;
    /** Returns true when the query is loading for any reason */
    isLoading: boolean;
    /** Returns true when the search query was changed until the new query's data has been loaded */
    isSearching: boolean;
    /** Function to directly update the generalQuery value used for the query */
    setGeneralQuery: Dispatch<SetStateAction<string>>;
    /** Call this to trigger fetching the next page */
    fetchMoreCollections: () => void;
}

/**
 * Fetches paginated collection options for filter bar usage, with debounced search.
 * Queries the style BFF for collections matching the given brand and search query,
 * and supports infinite scrolling via `fetchMoreCollections`.
 *
 * @param brandNumber - The brand number to filter collections by. Defaults to empty string.
 * @param skip - If true, skips the query entirely.
 * @returns collectionsOptions - The cumulative collection filter options across all fetched pages.
 * @returns isLoading - True when the query is loading for any reason.
 * @returns isSearching - True from when the search query changes until the debounced query finishes loading.
 * @returns setGeneralQuery - Setter to update the search query (debounced internally before querying).
 * @returns fetchMoreCollections - Call to fetch the next page of collections.
 */
const useCollectionsFilter = (
    brandNumber: string | undefined = '',
    skip?: boolean
): UseCollectionsFilterResult => {
    // We have two states here so we can debounce and prevent flooding the backend
    const [generalQuery, setGeneralQuery] = useState('');
    const [debouncedGeneralQuery, setDebouncedGeneralQuery] = useState('');

    // Fetch data for collections
    const {
        data: collectionsData,
        loading: isLoading,
        networkStatus: collectionsNetworkStatus,
        fetchMore,
    } = useQuery(GetCollectionOptionsDocument, {
        fetchPolicy: 'cache-and-network',
        notifyOnNetworkStatusChange: true,
        skip: skip ?? !brandNumber,
        context: {
            clientName: GraphClientNames['bestone-bi4-buying-style-bff'],
        },
        variables: {
            brandNumber,
            generalQuery: debouncedGeneralQuery,
            pagination: {
                first: COLLECTIONS_PAGE_SIZE,
                after: '',
            },
        },
    });

    const endCursor = collectionsData?.collections?.pageInfo?.endCursor ?? null;
    const hasNextPage =
        collectionsData?.collections?.pageInfo?.hasNextPage ?? false;

    /**
     * By comparing query and debounced query, this returns true immediately when typing.
     * However, it should stay true after the debounce and until we are done loading.
     * For this, we use the network status directly from the query.
     */
    const isSearching =
        generalQuery !== debouncedGeneralQuery ||
        collectionsNetworkStatus === NetworkStatus.setVariables;

    // Map the collections data to options for the filter
    const collectionsOptions: Array<FilterOption> = toFilterOptions({
        items: collectionsData?.collections?.edges?.map((edge) => edge?.node),
        label: (collection) =>
            `${collection.collectionTerm} (${collection.collectionName})`,
        value: (collection) => String(collection.collectionNumber ?? ''),
    });

    /** This function handles all logic to fetch the next page of collections */
    const fetchMoreCollections = (): void => {
        // If we are already loading, or there is no next page, don't fetch more
        if (isLoading || !hasNextPage) return;

        // Fetch the next page
        fetchMore({
            variables: {
                brandNumber,
                generalQuery: debouncedGeneralQuery,
                pagination: {
                    first: COLLECTIONS_PAGE_SIZE,
                    after: endCursor,
                },
            },
            updateQuery: (previousQueryResult, { fetchMoreResult }) => ({
                collections: {
                    totalCount:
                        previousQueryResult.collections?.totalCount ?? 0,
                    edges: [
                        ...(previousQueryResult.collections?.edges ?? []),
                        ...(fetchMoreResult.collections?.edges ?? []),
                    ],
                    pageInfo: fetchMoreResult.collections?.pageInfo,
                },
            }),
        }).catch(() => {
            throw new Error('Error fetching more collections');
        });
    };

    // Debounce the general query update for collections
    useEffect((): (() => void) => {
        const timeoutId = globalThis.setTimeout((): void => {
            setDebouncedGeneralQuery(generalQuery);
        }, 300);

        return (): void => {
            globalThis.clearTimeout(timeoutId);
        };
    }, [generalQuery]);

    return {
        collectionsOptions,
        isLoading,
        isSearching,
        setGeneralQuery,
        fetchMoreCollections,
    };
};

export { useCollectionsFilter };
